import React from "react";

/*
    Will make an array with objects from raw TAF.
    It extracts main wind and also change groups with wind info and also wind gusts
*/
export const formatTafToWindArray = (taf, isTrainingMode = false) => {
  if (!taf || typeof taf !== "string") return [];

  return isTrainingMode
    ? formatTrainingTafToWindArray(taf)
    : formatStandardTafToWindArray(taf);
};

/*
    Standard TAF format: TAF ICAO DDHHMMZ HHHH/HHHH wind...
*/
const formatStandardTafToWindArray = (taf) => {
  const result = [];

  // Regex for standard TAF main wind
  const mainWindRegex =
    /^TAF\s+\w+\s+\d{6}Z\s+(\d{4}\/\d{4})\s+(VRB|\d{3})(\d{2})(?:G(\d{2}))?KT/;
  const mainWindMatch = mainWindRegex.exec(taf);
  if (mainWindMatch) {
    result.push({
      title: `TAF ${mainWindMatch[1]}`,
      windDegrees:
        mainWindMatch[2] === "VRB" ? "VRB" : parseInt(mainWindMatch[2], 10),
      windSpeed: parseInt(mainWindMatch[3], 10),
      windGust: mainWindMatch[4] ? parseInt(mainWindMatch[4], 10) : null,
    });
    taf = taf.replace(mainWindMatch[0], "");
  }

  // Change groups for standard format
  const changeGroupRegex =
    /(PROB30|PROB40)?\s*(TEMPO|BECMG)?\s*(\d{4}\/\d{4})?\s*(VRB|\d{3})(\d{2})(?:G(\d{2}))?KT/g;

  let changeGroupMatch;
  while ((changeGroupMatch = changeGroupRegex.exec(taf)) !== null) {
    const probGroup = changeGroupMatch[1] ? `${changeGroupMatch[1]} ` : "";
    const groupType = changeGroupMatch[2] ? `${changeGroupMatch[2]} ` : "";
    const timePeriod = changeGroupMatch[3] ? `${changeGroupMatch[3]} ` : "";

    result.push({
      title: `TAF ${probGroup}${groupType}${timePeriod}`.trim(),
      windDegrees:
        changeGroupMatch[4] === "VRB"
          ? "VRB"
          : parseInt(changeGroupMatch[4], 10),
      windSpeed: parseInt(changeGroupMatch[5], 10),
      windGust: changeGroupMatch[6] ? parseInt(changeGroupMatch[6], 10) : null,
    });
  }

  return result;
};

/*
    Training TAF format - more permissive than standard format
*/
const formatTrainingTafToWindArray = (taf) => {
  const result = [];

  const windRegex = /(VRB|\d{3})(\d{2})(G\d{2})?KT/g;

  const winds = [];
  let match;
  while ((match = windRegex.exec(taf)) !== null) {
    const gustMatch = match[3] ? match[3].replace("G", "") : null;
    winds.push({
      index: match.index,
      windDegrees: match[1] === "VRB" ? "VRB" : parseInt(match[1], 10),
      windSpeed: parseInt(match[2], 10),
      windGust: gustMatch ? parseInt(gustMatch, 10) : null,
    });
  }

  winds.forEach((wind, idx) => {
    const textBefore = taf.substring(0, wind.index);

    let title = "TAF";

    // Check for PROB30/40 TEMPO combination
    if (/PROB30\s+TEMPO\s*$/i.test(textBefore)) {
      title = "TAF PROB30 TEMPO";
    } else if (/PROB40\s+TEMPO\s*$/i.test(textBefore)) {
      title = "TAF PROB40 TEMPO";
    } else if (/PROB30\s*$/i.test(textBefore)) {
      title = "TAF PROB30";
    } else if (/PROB40\s*$/i.test(textBefore)) {
      title = "TAF PROB40";
    } else if (/TEMPO\s*$/i.test(textBefore)) {
      title = "TAF TEMPO";
    } else if (/BECMG\s*$/i.test(textBefore)) {
      title = "TAF BECMG";
    }

    // Add time period if found (xxHH/xxHH or HHHH/HHHH)
    const timePeriodMatch = textBefore.match(
      /(xx\d{2}\/xx\d{2}|\d{4}\/\d{4})\s*$/
    );
    if (timePeriodMatch) {
      title += ` ${timePeriodMatch[1]}`;
    }

    result.push({
      title: title.trim(),
      windDegrees: wind.windDegrees,
      windSpeed: wind.windSpeed,
      windGust: wind.windGust,
    });
  });

  return result;
};

export const formatTafToHTML = (taf, airportName, shouldBeNativeHtml) => {
  const keywords = [
    `TAF ${airportName}`,
    `TAF AMD ${airportName}`,
    "BECMG",
    "TEMPO",
    "PROB30 TEMPO",
    "PROB40 TEMPO",
    "PROB30",
    "PROB40",
  ];

  const subStrings = taf
    .split(new RegExp(`(${keywords.join("|")})`, "gi"))
    .filter((string) => !!string); // Filter out falsey text sections (ex. empty string)

  const formattedString = subStrings.map((string, index) => {
    const isKeyword = keywords.includes(string);

    if (shouldBeNativeHtml) {
      const span = document.createElement("span");
      const strong = document.createElement("strong");
      const br = document.createElement("br");

      if (isKeyword) {
        if (index === 0) {
          // First text section should not be preceded by breakline
          strong.innerText = string;
          span.appendChild(strong);
        } else {
          span.appendChild(br);
          strong.innerText = string;
          span.appendChild(strong);
        }
      } else {
        span.textContent = string;
      }

      return span;
    } else {
      if (isKeyword) {
        if (index === 0) {
          // First text section should not be preceded by breakline
          return (
            <span key={index}>
              <strong>{string}</strong>
            </span>
          );
        }

        return (
          <span key={index}>
            <br />
            <strong>{string}</strong>
          </span>
        );
      }

      return <span key={index}>{string}</span>;
    }
  });

  return formattedString;
};
